/**
 * OAProductsApi - Batch product fetching with caching
 * Fetches products by handles or IDs using Storefront GraphQL API
 * Transforms to REST-compatible format for Vue components
 * Depends on OAStorefrontApi being loaded first
 */
window.OAProductsApi = {
  // key -> { at, value }. Always go through cacheGet / cacheSet / cacheHas: entries expire.
  cache: new Map(),

  // Availability is the perishable field. Held for the life of the page, this cache could hand
  // the back-in-stock sidebar a variant list still saying "available" for a size the fragment
  // half of the same switch had just rendered sold out. Matches pdp-color-switch.js's own TTL.
  CACHE_TTL_MS: 5 * 60 * 1000,

  // Ceiling on entries held at once. Expiry alone reclaims nothing — an entry is only TTL-checked
  // when its own key is read again — so a pointer sweeping a 30-colour rail pinned 30 transformed
  // products for the life of the page. Well above any one batch (a product is stored under both
  // its handle and its id, so a colour family counts twice).
  CACHE_MAX: 200,

  cacheGet: function(key) {
    var entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.at >= this.CACHE_TTL_MS) {
      this.cache.delete(key);
      return null;
    }
    return entry.value;
  },

  cacheHas: function(key) {
    return this.cacheGet(key) !== null;
  },

  cacheSet: function(key, value) {
    this.cache.set(key, { at: Date.now(), value: value });
    if (this.cache.size > this.CACHE_MAX) this.prune();
  },

  // handle -> the request that will cache it. The cache is only written on resolve, so without
  // this a pointer sweeping a colour rail fires a second full aliased document for handles whose
  // first request has not come back yet.
  inflight: {},

  // Claim `handles` for `promise` and release them when it settles, unless a later call has
  // already taken them over. Returns the promise unchanged for the caller that owns it; what is
  // stored is a non-rejecting copy, so a caller waiting on someone else's request does not
  // inherit its failure — every path here falls back to a cache read anyway.
  trackInflight: function(handles, promise) {
    var self = this;
    var tracked = promise.catch(function() { return null; });
    handles.forEach(function(h) { self.inflight[h] = tracked; });
    tracked.then(function() {
      handles.forEach(function(h) { if (self.inflight[h] === tracked) delete self.inflight[h]; });
    });
    return promise;
  },

  // Drop what has expired, then, if that was not enough, the oldest of what is left — a Map
  // iterates in insertion order, so its first keys are the least recently written. The cap sits
  // far above any one batch on purpose: fetchByHandles writes its results and reads them straight
  // back out, so evicting inside a batch would hand the caller nulls for what it just fetched.
  prune: function() {
    var self = this;
    var now = Date.now();
    this.cache.forEach(function(entry, key) {
      if (now - entry.at >= self.CACHE_TTL_MS) self.cache.delete(key);
    });
    if (this.cache.size <= this.CACHE_MAX) return;
    var excess = this.cache.size - this.CACHE_MAX;
    var keys = this.cache.keys();
    for (var i = 0; i < excess; i++) this.cache.delete(keys.next().value);
  },

  // Field set for fetching products by handle, as one aliased product(handle:) per handle: the
  // products(query: 'handle:"..."') filter is ignored on this store — verified live, it returns
  // the same arbitrary 100 products for every input — so a handle resolved only by luck, and every
  // other one fell through to REST after transferring 100 unrelated products in full.
  // product(handle:) is an exact lookup where null actually means "no such product".
  handleQueryFields: `
      id
      handle
      title
      tags
      productType
      availableForSale
      priceRange {
        minVariantPrice { amount currencyCode }
        maxVariantPrice { amount currencyCode }
      }
      compareAtPriceRange {
        minVariantPrice { amount }
      }
      images(first: 50) {
        nodes { url }
      }
      media(first: 10) {
        nodes {
          mediaContentType
          ... on Video {
            sources { url mimeType }
          }
        }
      }
      variants(first: 100) {
        nodes {
          id
          sku
          title
          price { amount }
          compareAtPrice { amount }
          availableForSale
          selectedOptions { name value }
        }
      }`,

  // The Storefront API rejects a whole document over its complexity budget with a 429, costed
  // from the `first:` values above — so the ceiling is fixed, and measured live with this field
  // set: 16 aliases pass, 18 fail. Ten leaves room for the selection to grow. A chunk that fails
  // is not fatal: its handles stay uncached and take the REST fallback.
  HANDLE_BATCH_SIZE: 10,

  // Query for fetching products by IDs
  idQuery: `query getProductsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        title
        tags
        productType
        availableForSale
        priceRange {
          minVariantPrice { amount currencyCode }
          maxVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount }
        }
        images(first: 50) {
          nodes { url }
        }
        media(first: 10) {
          nodes {
            mediaContentType
            ... on Video {
              sources { url mimeType }
            }
          }
        }
        variants(first: 100) {
          nodes {
            id
            sku
            title
            price { amount }
            compareAtPrice { amount }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
    }
  }`,

  /**
   * Transform GraphQL product to REST-compatible format
   * Matches structure expected by Vue components
   */
  transformProduct: function(gql) {
    if (!gql) return null;
    var self = this;

    var priceMin = Math.round(parseFloat(gql.priceRange.minVariantPrice.amount) * 100);
    var compareAtPrice = gql.compareAtPriceRange.minVariantPrice
      ? Math.round(parseFloat(gql.compareAtPriceRange.minVariantPrice.amount) * 100)
      : null;

    return {
      id: parseInt(OAStorefrontApi.toNumericId(gql.id)),
      handle: gql.handle,
      title: gql.title,
      tags: gql.tags,
      product_type: gql.productType,
      available: gql.availableForSale,
      price: priceMin,
      price_min: priceMin,
      compare_at_price: compareAtPrice,
      price_ratio: compareAtPrice && compareAtPrice > priceMin
        ? Math.round((1 - priceMin / compareAtPrice) * 100)
        : 0,
      variants: gql.variants.nodes.map(function(v) {
        return {
          id: parseInt(OAStorefrontApi.toNumericId(v.id)),
          sku: v.sku,
          title: v.title,
          option1: v.selectedOptions[0] ? v.selectedOptions[0].value : null,
          option2: v.selectedOptions[1] ? v.selectedOptions[1].value : null,
          option3: v.selectedOptions[2] ? v.selectedOptions[2].value : null,
          price: Math.round(parseFloat(v.price.amount) * 100),
          compare_at_price: v.compareAtPrice
            ? Math.round(parseFloat(v.compareAtPrice.amount) * 100)
            : null,
          inventory_quantity: v.availableForSale ? 1 : 0,
          available: v.availableForSale
        };
      }),
      product_image: gql.images.nodes[0] ? gql.images.nodes[0].url : null,
      image: gql.images.nodes[0] ? { src: gql.images.nodes[0].url } : null,
      images: gql.images.nodes.map(function(img) { return { src: img.url }; }),
      media: gql.media.nodes.map(function(m) {
        return {
          media_type: m.mediaContentType.toLowerCase(),
          sources: m.sources || []
        };
      })
    };
  },

  /**
   * Build the batched exact-lookup document: one aliased product(handle:) per handle. Aliases are
   * positional (p0, p1, …) — handles contain hyphens and cannot be GraphQL aliases directly — so
   * the caller maps results back by index.
   * @param {Array<string>} handles - Product handles
   * @returns {string} A complete GraphQL query document
   */
  buildHandleQuery: function(handles) {
    var fields = this.handleQueryFields;
    var selections = handles.map(function(h, i) {
      // JSON.stringify escapes the handle into a GraphQL string literal.
      return '\n  p' + i + ': product(handle: ' + JSON.stringify(h) + ') {' + fields + '\n  }';
    }).join('');
    return 'query getProductsByHandles {' + selections + '\n}';
  },

  /**
   * Normalize handle-like input to a Shopify product handle
   * Supports raw handles, /products/{handle}, and full product URLs
   * @param {string} value
   * @returns {string|null}
   */
  normalizeHandle: function(value) {
    if (typeof value !== 'string') return null;
    var handle = value.trim();
    if (!handle) return null;
    handle = handle.replace(/^https?:\/\/[^/]+/i, '');
    handle = handle.replace(/^\/?products\//i, '');
    handle = handle.split('?')[0].split('#')[0];
    handle = handle.replace(/^\/+|\/+$/g, '');
    return handle ? handle.toLowerCase() : null;
  },

  /**
   * REST fallback for product handle
   * @param {string} handle
   * @returns {Promise<Object|null>}
   */
  fetchByHandleRest: function(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function(response) {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function() { return null; });
  },

  /**
   * Fetch products by handles
   * @param {Array<string>} handles - Product handles to fetch
   * @returns {Promise<Array<Object|null>>} Products in same order as input handles
   */
  fetchByHandles: function(handles) {
    var self = this;
    if (!handles || handles.length === 0) {
      return Promise.resolve([]);
    }

    var requestedHandles = handles.map(function(h) { return self.normalizeHandle(h); });
    var validHandles = requestedHandles.filter(function(h) { return Boolean(h); });

    if (validHandles.length === 0) {
      return Promise.resolve(handles.map(function() { return null; }));
    }

    // Check cache first
    var uncached = validHandles.filter(function(h) { return !self.cacheHas(h); });

    if (uncached.length === 0) {
      return Promise.resolve(requestedHandles.map(function(h) { return h ? (self.cacheGet(h) || null) : null; }));
    }

    // Anything another call is already fetching is waited on rather than asked for again.
    var pending = [];
    var toFetch = [];
    uncached.forEach(function(h) {
      var flight = self.inflight[h];
      if (!flight) return toFetch.push(h);
      if (pending.indexOf(flight) === -1) pending.push(flight);
    });

    // Fetch the rest, in chunks small enough to stay inside the complexity budget.
    var chunks = [];
    for (var i = 0; i < toFetch.length; i += this.HANDLE_BATCH_SIZE) {
      chunks.push(toFetch.slice(i, i + this.HANDLE_BATCH_SIZE));
    }

    return Promise.all(pending.concat(chunks.map(function(chunk) {
      return self.trackInflight(chunk, OAStorefrontApi.fetch(self.buildHandleQuery(chunk), {}).then(function(data) {
        if (!data) return;

        // Aliases are positional, so chunk[i] is the handle that was asked for. A null alias
        // means no such product, and that handle drops through to the REST fallback below.
        chunk.forEach(function(handle, index) {
          var node = data['p' + index];
          if (!node) return;
          self.cacheSet(self.normalizeHandle(node.handle) || handle, self.transformProduct(node));
        });
      }));
    }))).then(function() {
      // Everything the GraphQL pass did not resolve — a missing product, or a chunk the API
      // rejected outright — retries individually over REST.
      var missing = uncached.filter(function(h) { return !self.cacheHas(h); });
      if (missing.length === 0) {
        return requestedHandles.map(function(h) { return h ? (self.cacheGet(h) || null) : null; });
      }

      return Promise.all(missing.map(function(h) {
        return self.trackInflight([h], self.fetchByHandleRest(h).then(function(product) {
          if (product) self.cacheSet(h, product);
        }));
      })).then(function() {
        return requestedHandles.map(function(h) { return h ? (self.cacheGet(h) || null) : null; });
      });
    });
  },

  /**
   * Fetch products by IDs
   * @param {Array<number|string>} ids - Product IDs to fetch
   * @returns {Promise<Array<Object|null>>} Products in same order as input IDs
   */
  fetchByIds: function(ids) {
    var self = this;
    if (!ids || ids.length === 0) {
      return Promise.resolve([]);
    }

    // Check cache first (by ID)
    var uncached = ids.filter(function(id) { return !self.cacheHas('id:' + id); });

    if (uncached.length === 0) {
      return Promise.resolve(ids.map(function(id) { return self.cacheGet('id:' + id); }));
    }

    // Fetch uncached products
    var gids = OAStorefrontApi.toGids('Product', uncached);
    return OAStorefrontApi.fetch(this.idQuery, { ids: gids })
      .then(function(data) {
        if (!data || !data.nodes) {
          return ids.map(function(id) { return self.cacheGet('id:' + id) || null; });
        }

        // Cache fetched products by ID and handle
        data.nodes.forEach(function(node) {
          if (node) {
            var product = self.transformProduct(node);
            var numericId = OAStorefrontApi.toNumericId(node.id);
            self.cacheSet('id:' + numericId, product);
            var normalizedNodeHandle = self.normalizeHandle(node.handle);
            if (normalizedNodeHandle) {
              self.cacheSet(normalizedNodeHandle, product);
            }
          }
        });

        // Return in original order
        return ids.map(function(id) { return self.cacheGet('id:' + id) || null; });
      });
  }
};
